import { useEffect, useState, ReactNode } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import api from "../utils/api";
import styles from "./DashboardPage.module.scss";
import Header from "../components/Header/Header";
import Footer from "../components/Footer/Footer";
import Sidebar from "../components/Sidebar/sidebar";
import { useAuth } from "../context/AuthContext";

interface Stats {
  users: number;
  organizations: number;
  apps: number;
  orgRoleTypes: number;
  userTypes: number;
  appAccessLevels: number;
  orgConnections: number;
  userConnections: number;
}

interface DashboardProps {
  children?: ReactNode;
}

const DashboardPage: React.FC<DashboardProps> = ({ children }) => {
  const [stats, setStats] = useState<Stats | null>(null);
  const [isSidebarExpanded, setIsSidebarExpanded] = useState(false);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string>("");
  const navigate = useNavigate();
  const location = useLocation();
  const { isAuthenticated, user, loading: authLoading } = useAuth();

  const fetchStats = async () => {
    try {
      setError("");
      if (!isAuthenticated) {
        navigate("/login", { replace: true });
        return;
      }
      const res = await api.get<{ message: string; stats: Stats }>("/core/dashboard/stats");
      if (!res) {
        throw new Error("No response from /core/dashboard/stats");
      }
      setStats(res.data.stats || null);
    } catch (err: any) {
      const errorMessage = err.response?.data?.error || err.message || "Failed to fetch dashboard stats";
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!authLoading && !children && location.pathname === "/dashboard") {
      fetchStats();
    } else if (!authLoading) {
      setLoading(false);
    }
  }, [children, authLoading, user, isAuthenticated, location.pathname]);

  if (authLoading) {
    return <div>Loading...</div>;
  }

  return (
    <div className={styles.dashboard}>
      <Sidebar
        isExpanded={isSidebarExpanded}
        toggleSidebar={() => setIsSidebarExpanded(!isSidebarExpanded)}
      />
      <div className={`${styles.mainContent} ${isSidebarExpanded ? styles.expanded : ""}`}>
        <Header isLoggedIn={true} />
        <div className={styles.contentWrapper}>
          {children ? (
            children
          ) : loading ? (
            <div className={styles.content}>Loading...</div>
          ) : error ? (
            <div className={styles.content}>
              <h3 className={styles.title}>Dashboard</h3>
              <div className={styles.error}>{error}</div>
              <button
                onClick={fetchStats}
                className={styles.actionButton}
              >
                Retry
              </button>
            </div>
          ) : (
            <div className={styles.content}>
              <h3 className={styles.title}>Dashboard</h3>
              <div className={styles.cardGrid}>
                {user?.issuperadmin ? (
                  <>
                    <div className={styles.card}>
                      <h4 className={styles.cardTitle}>Users</h4>
                      <p className={styles.cardValue}>{stats?.users || 0}</p>
                      <button
                        onClick={() => navigate("/users")}
                        className={styles.actionButton}
                      >
                        Manage Users
                      </button>
                    </div>
                    <div className={styles.card}>
                      <h4 className={styles.cardTitle}>Organizations</h4>
                      <p className={styles.cardValue}>{stats?.organizations || 0}</p>
                      <button
                        onClick={() => navigate("/organizations")}
                        className={styles.actionButton}
                      >
                        Manage Organizations
                      </button>
                    </div>
                    <div className={styles.card}>
                      <h4 className={styles.cardTitle}>Apps</h4>
                      <p className={styles.cardValue}>{stats?.apps || 0}</p>
                      <button
                        onClick={() => navigate("/apps")}
                        className={styles.actionButton}
                      >
                        Manage Apps
                      </button>
                    </div>
                    <div className={styles.card}>
                      <h4 className={styles.cardTitle}>Organization Role Types</h4>
                      <p className={styles.cardValue}>{stats?.orgRoleTypes || 0}</p>
                      <button
                        onClick={() => navigate("/roles")}
                        className={styles.actionButton}
                      >
                        Manage Roles
                      </button>
                    </div>
                    <div className={styles.card}>
                      <h4 className={styles.cardTitle}>User Types</h4>
                      <p className={styles.cardValue}>{stats?.userTypes || 0}</p>
                      <button
                        onClick={() => navigate("/roles")}
                        className={styles.actionButton}
                      >
                        Manage Roles
                      </button>
                    </div>
                    <div className={styles.card}>
                      <h4 className={styles.cardTitle}>App Access Levels</h4>
                      <p className={styles.cardValue}>{stats?.appAccessLevels || 0}</p>
                      <button
                        onClick={() => navigate("/roles")}
                        className={styles.actionButton}
                      >
                        Manage Roles
                      </button>
                    </div>
                    <div className={styles.card}>
                      <h4 className={styles.cardTitle}>Connections</h4>
                      <div className={styles.connectionStats}>
                        <span className={styles.connectionItem}>Org: {stats?.orgConnections || 0}</span>
                        <span className={styles.connectionItem}>User: {stats?.userConnections || 0}</span>
                      </div>
                      <button
                        onClick={() => navigate("/connections")}
                        className={styles.actionButton}
                      >
                        Manage Connections
                      </button>
                    </div>
                  </>
                ) : (
                  <div className={styles.error}>Access denied: Superadmin privileges required</div>
                )}
              </div>
            </div>
          )}
          <Footer isLoggedIn={true} />
        </div>
      </div>
    </div>
  );
};

export default DashboardPage;
